import { RowAction } from '@banno/jha-wc/src/tables/advanced/jha-advanced-table/jha-advanced-table.types';
import { html } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { TmTableRowAction } from '../../components/jha-wc/tm-table';

type RowActionDetail<T> = { item: T; action: string };
type handleRowAction<T> = (event: CustomEvent<RowActionDetail<T>>) => void;

/**
 * Map a list of row actions to a list of TmTableRowAction objects.
 *
 * @param rowActions The list of row actions to map.
 * @param handleRowAction The function to handle the row action.
 * @returns A list of TmTableRowAction objects.
 */
export function mapToTmTableRowActions<T>(
    rowActions: RowAction[],
    handleRowAction: handleRowAction<T>
) {
    return rowActions.map(action => {
        const rowAction: TmTableRowAction<T> = {
            label: action.label,
            isHidden:
                typeof action.hiddenCondition === 'function'
                    ? (item: T) =>
                          (action.hiddenCondition as (item: Record<string, unknown>) => boolean)(
                              item as Record<string, unknown>
                          )
                    : action.hiddenCondition,
            isDisabled:
                typeof action.disabledCondition === 'function'
                    ? (item: T) =>
                          (action.disabledCondition as (item: Record<string, unknown>) => boolean)(
                              item as Record<string, unknown>
                          )
                    : action.disabledCondition,
            execute: (item: T) => {
                // Mapping Rule: The action id specifies the type here
                const event = new CustomEvent<RowActionDetail<T>>('row-action', {
                    detail: { item, action: action.id },
                });
                handleRowAction(event);
            },
        };
        return rowAction;
    });
}

export function renderTag(value?: unknown) {
    if (!value) {
        throw new Error('Could not render tag for an empty value');
    }

    return html`<jh-tag
        style=${styleMap({
            '--jh-tag-color-background-enabled': value === 'Active' ? '#ecf7e7' : '#eeeeee',
            '--jh-tag-color-text-enabled': value === 'Active' ? '#366429' : '#5F6468',
        })}
        .label=${value.toString()}
    ></jh-tag>`;
}

export function renderNoContent(message: string) {
    return html`<div class="flex flex-col gap-4 justify-center items-center">
        <jh-icon-magnifying-glass size="large"></jh-icon-magnifying-glass>
        <p>${message}</p>
    </div>`;
}

/**
 * Create a function that will dynamically derive the label for the current view.
 *
 * @param label The static label text with which to append the item count.
 * @returns A partially-applied function that will derive the view label based
 * on the current item count.
 */
export function deriveCountLabel<T>(label: string) {
    return function (items?: T[]) {
        if (!items) {
            return label;
        }

        return `${label} (${items.length})`;
    };
}

/**
 * De-duplicate an array of accounts based on
 * an identity criteria other than object reference.
 */
export function dedupe<A extends { id: number }>(items: A[]) {
    const tuples = items.map(a => [a.id, a] as const);

    // use a dict to de-dupe based on DTO ID
    const map = new Map(tuples);

    return Array.from(map.values()).sort();
}
